import { AdminRolesDialog } from "@/components/AdminRolesDialog";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { setResidentOverride } from "@/lib/admin.functions";
import { STATUS_TIERS, tierByKey } from "@/lib/drives";

function norm(s: string | null | undefined): string {
  return (s ?? "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/\s+/g, " ")
    .trim();
}


type AdminResidentRow = {
  user_id: string;
  full_name: string | null;
  username: string | null;
  email: string | null;
  manual_status_override: string | null;
  drives: number;
  computed_key: string;
  current_key: string;
};
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/residents")({
  component: AdminResidentsPage,
});

const OVERRIDE_OPTIONS = [
  { value: "__none__", label: "— (по драйвам)" },
  ...STATUS_TIERS.map((t) => ({ value: t.key, label: t.label })),
];

function AdminResidentsPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) {
        navigate({ to: "/home" });
        return;
      }
      const { data: prof } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("user_id", uid)
        .maybeSingle();
      if (!(prof as { is_admin?: boolean } | null)?.is_admin) {
        navigate({ to: "/home" });
        return;
      }
      setAuthorized(true);
    })();
  }, [navigate]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "residents"],
    enabled: authorized === true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_residents");
      if (error) throw error;
      return { rows: (data ?? []) as AdminResidentRow[] };
    },
  });

  const { data: roleCounts } = useQuery({
    queryKey: ["admin", "role-counts"],
    enabled: authorized === true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("role_counts");
      if (error) throw error;
      return (data ?? []) as {
        role_key: string;
        display_title: string;
        kind: string;
        icon: string;
        sort_order: number;
        project_id: string | null;
        project_title: string | null;
        holders: number;
      }[];
    },
  });

  const sortedRoleCounts = useMemo(() => {
    const all = roleCounts ?? [];
    return [...all].sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === "role" ? -1 : 1;
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.display_title.localeCompare(b.display_title, "ru");
    });
  }, [roleCounts]);

  const [filter, setFilter] = useState("");
  const allRows = data?.rows ?? [];
  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const q = norm(filter);
    if (!q) return all;
    const tokens = q.split(" ").filter(Boolean);
    return all.filter((r) => {
      const hay = `${norm(r.full_name)} ${norm(r.email)} ${norm(r.username)}`;
      return tokens.every((t) => hay.includes(t));
    });
  }, [data, filter]);

  const stats = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of data?.rows ?? []) {
      const key = r.manual_status_override ?? r.computed_key;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return STATUS_TIERS.map((t) => ({ label: t.label, count: counts.get(t.key) ?? 0 }));
  }, [data]);

  const topDrives = useMemo(() => {
    const all = data?.rows ?? [];
    return [...all].sort((a, b) => b.drives - a.drives).slice(0, 10);
  }, [data]);

  if (authorized !== true) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 text-sm text-td-muted">
        Проверка доступа…
      </div>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-8">
      <button
        type="button"
        onClick={() => navigate({ to: "/home" })}
        className="mb-4 text-sm text-td-muted hover:text-td-dark"
      >
        ← Назад
      </button>
      <h1 className="mb-2 text-2xl font-semibold text-td-dark">Статусы резидентов</h1>
      <p className="mb-4 text-sm text-td-muted">
        Ручное переопределение отображаемого статуса. Драйвы продолжают начисляться независимо.
      </p>
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-td-line bg-td-soft/40 px-3 py-2 text-sm text-td-dark">
        <span className="text-xs uppercase tracking-wide text-td-muted">Статусы:</span>
        {stats.map((s) => (
          <span key={s.label} className="rounded-full bg-white px-2 py-0.5">
            {s.label} — <b>{s.count}</b>
          </span>
        ))}
        <span className="text-xs text-td-muted">всего {allRows.length}</span>
      </div>
      {sortedRoleCounts.length > 0 ? (
        <Link
          to="/admin/roles"
          className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-td-line bg-td-soft/40 px-3 py-2 text-sm text-td-dark transition hover:bg-td-soft/70"
        >
          <span className="text-xs uppercase tracking-wide text-td-muted">Роли и титулы:</span>
          {sortedRoleCounts.map((r) => (
            <span
              key={`${r.kind}-${r.role_key}-${r.project_id ?? "none"}`}
              className={`rounded-full bg-white px-2 py-0.5 ${r.holders === 0 ? "text-td-muted" : ""}`}
            >
              {r.icon} {r.display_title} — <b>{r.holders}</b>
            </span>
          ))}
          <span className="ml-auto whitespace-nowrap text-xs text-td-muted">Подробнее →</span>
        </Link>
      ) : null}
      {!isLoading && topDrives.length > 0 ? (
        <div className="mb-4 overflow-hidden rounded-2xl border border-td-line bg-white">
          <div className="border-b border-td-line bg-td-soft/40 px-3 py-2 text-xs uppercase tracking-wide text-td-muted">
            Топ-10 по драйвам
          </div>
          <ol className="divide-y divide-td-line">
            {topDrives.map((r, i) => (
              <li key={r.user_id} className="flex items-center gap-3 px-3 py-2">
                <span
                  className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    i === 0
                      ? "bg-amber-100 text-amber-700"
                      : i === 1
                        ? "bg-zinc-200 text-zinc-700"
                        : i === 2
                          ? "bg-orange-100 text-orange-700"
                          : "bg-td-soft/60 text-td-muted"
                  }`}
                >
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-td-dark">{r.full_name ?? "—"}</div>
                  {r.username ? <div className="text-xs text-td-muted">@{r.username}</div> : null}
                </div>
                <span className="shrink-0 text-sm text-td-dark">{r.drives} 🔥</span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <Input
        placeholder="Поиск по имени или email"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="mb-4 max-w-md"
      />

      {isLoading ? (
        <div className="text-sm text-td-muted">Загружаем…</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-td-line bg-white">
          <table className="w-full text-sm">
            <thead className="bg-td-soft/40 text-left text-xs uppercase tracking-wide text-td-muted">
              <tr>
                <th className="px-3 py-2">Имя</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">По драйвам</th>
                <th className="px-3 py-2">Override</th>
                <th className="px-3 py-2 w-72">Изменить</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ResidentRow key={row.user_id} row={row} />
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-td-muted">
                    Никого не нашли.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

function ResidentRow({ row }: { row: AdminResidentRow }) {
  const queryClient = useQueryClient();
  const saveOverride = useServerFn(setResidentOverride);
  const [value, setValue] = useState<string>(row.manual_status_override ?? "__none__");
  const [saving, setSaving] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);

  const dirty = (row.manual_status_override ?? "__none__") !== value;

  async function handleSave() {
    setSaving(true);
    try {
      await saveOverride({
        data: { userId: row.user_id, override: value === "__none__" ? null : value },
      });
      toast.success("Сохранено");
      queryClient.invalidateQueries({ queryKey: ["admin", "residents"] });
      queryClient.invalidateQueries({ queryKey: ["resident-status"] });

    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr className="border-t border-td-line">
      <td className="px-3 py-2">
        <div className="font-medium text-td-dark">{row.full_name ?? "—"}</div>
        {row.username ? <div className="text-xs text-td-muted">@{row.username}</div> : null}
      </td>
      <td className="px-3 py-2 text-td-muted">{row.email ?? "—"}</td>
      <td className="px-3 py-2">
        <span className="text-td-dark">{tierByKey(row.computed_key)?.label ?? "—"}</span>
        <span className="ml-1 text-xs text-td-muted">({row.drives} 🔥)</span>
      </td>
      <td className="px-3 py-2">
        {row.manual_status_override ? (
          <span className="text-td-dark">{tierByKey(row.manual_status_override)?.label ?? "—"}</span>
        ) : (
          <span className="text-td-muted">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <Select value={value} onValueChange={setValue}>
            <SelectTrigger className="h-9 flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OVERRIDE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? "…" : "OK"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setRolesOpen(true)}>
            Роли
          </Button>
        </div>
        <AdminRolesDialog userId={row.user_id} name={row.full_name} open={rolesOpen} onOpenChange={setRolesOpen} />
      </td>
    </tr>
  );
}
